// Display helpers for dates and amounts.
//
// The dates are ISO strings, because rows.js revives each `$date` to one. The
// CVR dates without a time are stored at midnight UTC (`stiftelsesDato`
// -78883200000 = 1967-07-03T00:00:00Z), so a calendar date shows in UTC. In a
// time zone west of UTC, a local date shows the day before.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY = 86400;
const MONTH = 30.4375 * DAY;
const YEAR = 365.25 * DAY;

function toDate(value) {
  if (value === null || value === undefined || value === '') return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** "30s", "45m", "10h" for the last day; else "5 Mar", or "22 Nov 2013" for an older year. */
export function timeSince(value, now = new Date()) {
  const date = toDate(value);
  if (!date) return '';
  const seconds = (now.getTime() - date.getTime()) / 1000;
  if (seconds >= 0 && seconds < 60) return `${Math.floor(seconds)}s`;
  if (seconds >= 0 && seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds >= 0 && seconds <= DAY) return `${Math.floor(seconds / 3600)}h`;
  const year = date.getUTCFullYear() === now.getUTCFullYear() ? '' : ` ${date.getUTCFullYear()}`;
  return `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]}${year}`;
}

/** The time from `start` to `end`, or to now when `end` is empty: "12d", "3M", "10y". */
export function lifetime(start, end, now = new Date()) {
  const from = toDate(start);
  if (!from) return '';
  const to = toDate(end) ?? now;
  const seconds = (to.getTime() - from.getTime()) / 1000;
  if (seconds < 60) return `${Math.max(0, Math.floor(seconds))}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds <= DAY) return `${Math.floor(seconds / 3600)}h`;
  if (seconds <= 30 * DAY) return `${Math.floor(seconds / DAY)}d`;
  if (seconds < YEAR) return `${Math.floor(seconds / MONTH)}M`;
  return `${Math.floor(seconds / YEAR)}y`;
}

/**
 * Round axis ticks from `lo` to `hi`: about `count` steps of 1, 2 or 5 times a
 * power of ten. The first tick is at or below `lo`, the last at or above `hi`.
 */
export function niceTicks(lo, hi, count = 4) {
  if (!(hi > lo)) hi = lo + 1;
  const raw = (hi - lo) / count;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const norm = raw / magnitude;
  const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10) * magnitude;
  const first = Math.floor(lo / step) * step;
  const last = Math.ceil(hi / step) * step;
  const ticks = [];
  for (let v = first; v <= last + step / 2; v += step) ticks.push(Number(v.toPrecision(12)) || 0);
  return ticks;
}

/** An axis label: 2000000 -> "2 mio.", 1500000 -> "1,5 mio.", 200000 -> "200 t.". */
export function compact(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  const short = (v) => String(Math.round(v * 10) / 10).replace('.', ',');
  if (Math.abs(n) >= 1e6) return `${short(n / 1e6)} mio.`;
  if (Math.abs(n) >= 1e3) return `${short(n / 1e3)} t.`;
  return short(n);
}

/** 1234567 -> "1.234.567", the Danish group separator. */
export function thousands(value) {
  if (value === null || value === undefined || value === '') return '';
  const [whole, fraction] = String(value).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return fraction === undefined ? grouped : `${grouped},${fraction}`;
}

const DANISH_MONTHS = ['jan.', 'feb.', 'mar.', 'apr.', 'maj', 'jun.', 'jul.', 'aug.', 'sep.', 'okt.', 'nov.', 'dec.'];

/** "2024-12-19" -> "19. dec. 2024". The day of the CVR date, with no time zone shift. */
export function danishDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ''));
  if (!match) return '';
  return `${Number(match[3])}. ${DANISH_MONTHS[Number(match[2]) - 1]} ${match[1]}`;
}

/**
 * Kroner: "1.118.950 kr.", "223,79 kr.", and from ten million "10,1 mio. kr.". An amount
 * paid in stays exact; only a large estimate (the value of a company) gets rounded.
 */
export function money(value) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '';
  const n = Number(value);
  if (Math.abs(n) >= 1e7) return `${(n / 1e6).toFixed(1).replace('.', ',')} mio. kr.`;
  return `${thousands(Number(n.toFixed(2)))} kr.`;
}

/**
 * The chart shows every n-th x label, so the labels do not run into each other.
 * `width` is the width of the plot area in px; a label needs about `px` px.
 */
export function labelStep(count, width, px = 36) {
  return Math.min(count, Math.max(1, Math.ceil((count * px) / width)));
}
